// @flow

import type {Dispatch, GetState} from '../../lib/definitions/redux'
import type {AuthToken} from '../../lib/definitions/identity'
import type {AuthState} from '../reducers/auth'
import * as loadingActions from './loading'

import * as loginAPI from '../../lib/api/login'
import {syncToken as syncTokenLegacy} from '../../lib/api/legacyWebsite'

import {pushRoute} from './router'

import {APP_RESET_STATE} from './app'
import {promise as loadingPromise} from './loading'
import getDeviceInfo from '../../lib/shim/deviceInfo'

import generateUUID from 'uuid'

export const AUTH_FORM_FIELD_CHANGE= 'AUTH_FORM_FIELD_CHANGE'
export const VALIDATE_LOGIN_FORM= 'VALIDATE_LOGIN_FORM'
export const CLEAR_PASSWORD_ERROR_MESSAGE= 'CLEAR_PASSWORD_ERROR_MESSAGE'

// these are the state of the login form
export const LOGIN_REQUEST= 'LOGIN_REQUEST'
export const LOGIN_SUCCESS= 'LOGIN_SUCCESS'
export const LOGIN_FAILURE= 'LOGIN_FAILURE'
export const LOGIN_LOCK= 'LOGIN_LOCK'

export const LOGIN_SET_TOKEN = 'LOGIN_TOKEN_SET'
export const LOGIN_MUST_RESET_PASS_ERROR_CODE = 'The unicorn has lost it\'s horn'
export const LOGIN_MUST_RESET_PIN_ERROR_CODE = 'The pink unicorn has lost it\'s horn'
export const INVALID_USERNAME_OR_PASSWORD = 'Invalid username or password'

export const LOGIN_REMOVE_PIN = 'LOGIN_REMOVE_PIN'
export const LOGIN_ENABLE_PIN = 'LOGIN_ENABLE_PIN'

export const LOGIN_REMOVE_FINGERPRINT = 'LOGIN_REMOVE_FINGERPRINT'
export const LOGIN_ENABLE_FINGERPRINT = 'LOGIN_ENABLE_FINGERPRINT'

export const LOGIN_CLEAR_FIRST_LOGIN = 'LOGIN_CLEAR_FIRST_LOGIN'

export const LOGIN_SET_CAPTCHA = 'LOGIN_SET_CAPTCHA'
export const LOGIN_CAPTCHA_EXPIRED = 'LOGIN_CAPTCHA_EXPIRED'

export const LOGIN_PREVENT_OLD_VERSION = 'LOGIN_PREVENT_OLD_VERSION'
export const LOGIN_SET_AFTER_LOGIN_ROUTE = 'LOGIN_SET_AFTER_LOGIN_ROUTE'
export const LOGIN_SYNC_TOKEN_WITH_LEGACY = 'LOGIN_SYNC_TOKEN_WITH_LEGACY'


export function authFormFieldChange(field : string, value : ?string){
	return {
		type: AUTH_FORM_FIELD_CHANGE,
		data: {field: field, value: value}
	}
}

export function clearPasswordErrorMessage(){
	return {
		type: CLEAR_PASSWORD_ERROR_MESSAGE,
		field: 'password'
	}
}

export function validateLoginForm(username : string, password: string){
	return {
		type: VALIDATE_LOGIN_FORM,
		data: {username: username, password:password}
	}
}

export function login(username : string, password : string, passwordAsPin : boolean){
	return (dispatch : Dispatch, getState : GetState) => {
		const Auth : AuthState  = getState().Auth

		// Validate login
		dispatch(validateLoginForm(username, password))

		// Check for username errors
		if(!Auth || Auth.usernameErrorMessage || Auth.passwordErrorMessage){
			dispatch(loginFailure())
			return Promise.reject("No Auth or Username/Password error")
		}

		dispatch(loginRequest())

		return loginAPI.login(username, password, getDeviceInfo().getUniqueID(), passwordAsPin, Auth.captcha)
			.then(login => {
				if(login.token){
					dispatch(loginSuccess(login.token))
				}else{
					dispatch(loginFailure(login.error))
				}
				return login
			})
			.catch(e => dispatch(loginFailure(e.message)))
	}
}

/**
 * Dispatches the staff login request, after the SSO redirect has happened
 * @param code
 * @param callbackUrl
 * @return {function(Dispatch)}
 */
export function staffLogin(code : string, callbackUrl : string){
	return (dispatch : Dispatch) => {
		dispatch(loginRequest())

		return loginAPI.loginStaff(code, callbackUrl)
			.then(login => {
				if(login.token){
					dispatch(loginSuccess(login.token))
				}else{
					dispatch(loginFailure(login.error))
				}
				return login
			})
			.catch(e => dispatch(loginFailure(e.message)))
	}
}

/**
 * Sync the token with the old members website
 * @param token
 * @return {function(Dispatch): (Promise.<T>|Promise.<T>)}
 */
export function syncTokenWithLegacy(token : string){
	return (dispatch : Dispatch) =>
		loadingActions.promise(
			dispatch,
			LOGIN_SYNC_TOKEN_WITH_LEGACY,
			syncTokenLegacy(token)
		)
}

/**
 * Triggers a login call with the saved secret data for fingerprint
 * @return {function(*, *)}
 */
export function loginFingerprint(){
	return (dispatch : Dispatch, getState : GetState) => {
		const {username, fingerprintSecret} = getState().Auth
		return dispatch(login(username, fingerprintSecret, true))
	}
}

function loginRequest(){
	return {
		type: LOGIN_REQUEST
	}
}

function loginSuccess(token : AuthToken){
	if(token === undefined){
		throw new Error("Token must be passed to success")
	}

	return {
		type: LOGIN_SUCCESS,
		token: token
	}
}

function loginFailure(err : ?string){
	return {
		type: LOGIN_FAILURE,
		error: err
	}
}

export function logout(){
	return (dispatch : Dispatch, getState : GetState) => {
		const token = getState().Auth.token
		dispatch({type: APP_RESET_STATE})

		return token ? loginAPI.logout(token) : Promise.resolve()
	}
}

/**
 * Locks the MyBudget Application
 * @returns {function()}
 */
export function showLockScreen(){
	return (dispatch: Dispatch) => {
		dispatch({
			type: LOGIN_LOCK
		})

		dispatch(pushRoute('Login'))
	}
}

/**
 * Refreshes the refresh token
 * @returns {function(): Promise.<*>}
 */
export function refreshToken(){
	return (dispatch : Dispatch, getState : GetState) =>
		loadingPromise(dispatch, LOGIN_SET_TOKEN,
			loginAPI.refresh(getState().Auth.token)
				.then((result) => dispatch(_setToken(result.token)))
		)

}

function _setToken(token : string){
	return {
		type: LOGIN_SET_TOKEN,
		token: token
	}
}

/**
 * Remove the secondary auth logins from this device
 * @return {function(*=, *): Promise.<TResult>}
 */
export function disablePIN(){
	return (dispatch: Dispatch) =>
		loadingPromise(
			dispatch,
			LOGIN_REMOVE_PIN,
			loginAPI.removeSecondaryAuthAccess(getDeviceInfo().getUniqueID(), 'pin')
		).then(() => dispatch(disablePinUI()))
}

/**
 * Remove the secondary auth logins from this device
 * @return {function(*=, *): Promise.<TResult>}
 */
export function disableFingerprint(){
	return (dispatch: Dispatch) =>
		loadingPromise(
			dispatch,
			LOGIN_REMOVE_FINGERPRINT,
			loginAPI.removeSecondaryAuthAccess(getDeviceInfo().getUniqueID(), 'fingerprint')
		)
			.catch(() => {}) // Doesn't matter if the server removes it
			.then(() => dispatch(disableFingerprintUI()))
}



/**
 * Enables Fingerprint login and sends a secret ID to the server to be used as a PIN
 * @return {{type: string}}
 */
export function enableFingerprint(){
	return (dispatch: Dispatch) => {
		const secret = generateUUID.v4()

		return loadingPromise(
			dispatch,
			LOGIN_ENABLE_FINGERPRINT,
			loginAPI.createSecondaryAuthAccess(secret, getDeviceInfo().getUniqueID(), 'fingerprint')
		).then(() => dispatch(_enableFingerprint(secret)))
	}
}

export function _enableFingerprint(secret: string){
	return {
		type: LOGIN_ENABLE_FINGERPRINT,
		secret: secret
	}
}

/**
 * Enables PIN login
 * @return {{type: string}}
 */
export function enablePinUI(){
	return {
		type: LOGIN_ENABLE_PIN
	}
}

/**
 * Disables PIN login UI
 */
export function disablePinUI(){
	return {
		type: LOGIN_REMOVE_PIN
	}
}

export function disableFingerprintUI(){
	return {
		type: LOGIN_REMOVE_FINGERPRINT
	}
}

export function clearFirstLogin(){
	return {
		type: LOGIN_CLEAR_FIRST_LOGIN
	}
}

/**
 * Set the captcha value to be sent in the next auth request
 * @param captchaResponse
 * @returns {{type: string, captcha: *}}
 */
export function setCaptcha(captchaResponse : string){
	return {
		type: LOGIN_SET_CAPTCHA,
		captcha: captchaResponse
	}
}

/**
 * Prevents an older verison from logging in
 * @return {{type: string}}
 */
export function preventLoginOldVersion(){
	return {
		type: LOGIN_PREVENT_OLD_VERSION
	}
}

/**
 * Sets the after login route
 * @param name
 * @param meta
 * @return {{type: string, route: {name: string, meta: Object}}}
 */
export function setAfterLoginRoute(name : string, meta: Object = {}){
	return {
		type: LOGIN_SET_AFTER_LOGIN_ROUTE,
		route: {
			name: name,
			meta: meta
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/mb-app-common/store/actions/auth.js